// ============================================================================
// PRELIT-CASE-002 — Pre-Litigation Case Detail (XL Modal)
// ============================================================================
// Single-case workspace, mounted globally as an XL modal in main_layout.
// Layout (top to bottom):
//   • Compact header: title + status pill (left) · Refresh + Close (right)
//   • Subtitle line: customer · account · product · branch
//   • Flat responsive 3-4 col field grid (always-on, no collapse)
//   • Linear 4-step workflow stepper (Claimed → Contacting → Commitment → Closed)
//   • Action row: Log Action · Capture Commitment · Reassign · Escalate ▾
//   • Tabs: Overview · Actions · Commitments · Timeline · Audit
//   • Footer: View Account 360 · All Cases · Close
//
// Workflow transitions are auto-advanced server-side (LogActionAsync and
// CaptureCommitmentAsync auto-fire start_contacting from ASSIGNED), so the
// EWF action bar is no longer surfaced — domain buttons drive everything.
//
// Opened via the openCaseDetail bridge (openCaseDetail.ts), which dispatches
// the 'recsuit:open-case-detail-modal' window event. main_layout listens for
// that event and toggles this modal open with the caseId.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// (useNavigate retired — case-detail footer no longer navigates to account 360.)
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { WorkflowTimeline } from 'recsuit-workflow';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import {
  usePreLitCase,
} from 'hooks/api/usePreLitigation';
import { usePreLitConfig } from 'hooks/api/usePreLitConfig';
import { useRecsuitPermissions } from 'hooks/useRecsuitPermissions';
import type { PreLitCase, AgentAction, Commitment, EscalationTarget } from 'api/types/preLitigation.types';
import { preLitigationService } from 'api/services/preLitigationService';
import LogAgentActionModal from 'components/modals/modules/pre-litigation-module/LogAgentActionModal';
import CaptureCommitmentModal from 'components/modals/modules/pre-litigation-module/CaptureCommitmentModal';
import ResolveCommitmentModal from 'components/modals/modules/pre-litigation-module/ResolveCommitmentModal';
import EscalateCaseModal from 'components/modals/modules/pre-litigation-module/EscalateCaseModal';
import AssignAgentModal from 'components/modals/modules/pre-litigation-module/AssignAgentModal';
import CloseEarlyRecoveryModal from 'components/modals/modules/pre-litigation-module/CloseEarlyRecoveryModal';
// (openAccountCases import retired — All Cases button removed from the footer.)
import StatusPill, { type PillTone } from 'components/common/StatusPill';
// (WorkflowStepper retired — the status pill + inline SLA chip in the modal
//  header now convey the current state without consuming a row.)
import EscalateDropdown from 'components/common/EscalateDropdown';
// (ActionTimelineCard / CommitmentCard imports retired — the unified activity
//  grid renders these rows inline.)

type TabId = 'overview' | 'actions' | 'commitments' | 'timeline' | 'audit';

interface PreLitigationCaseDetailModalProps {
  isOpen: boolean;
  caseId: string | null;
  onClose: () => void;
  // Minimise support — mirrors the Zone form modal pattern. The modal title
  // bar gains a chevronDown button when `tabId` is provided. Clicking it
  // parks the case detail in the originating tab's MinimizedModalStrip
  // (rendered by Pre-Lit Pool / Cases ModuleHeaders).
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (caseId: string, activeTab: TabId) => void;
}

const PRELIT_CASE_MODULE = 'pre-litigation-case';

// ----------------------------------------------------------------------------
// Status pill: one source of truth for the header badge
// ----------------------------------------------------------------------------

function caseStatusPill(kase: PreLitCase): { label: string; tone: PillTone; pulse: boolean } {
  // Closed cases — surface the disposition.
  if (kase.caseStatus === 'CLOSED') {
    if (kase.currentStateCode === 'CLOSED_RECOVERED') return { label: 'Closed · Recovered',  tone: 'success', pulse: false };
    if (kase.currentStateCode === 'ESCALATED') {
      const target = (kase as any)?.metaData?.escalation_target_type ?? null;
      return { label: target ? `Closed · Escalated to ${target}` : 'Closed · Escalated', tone: 'danger', pulse: false };
    }
    if ((kase as any).caseStatusCode === 'SETTLED')   return { label: 'Closed · Settled (Manual)',   tone: 'success', pulse: false };
    if ((kase as any).caseStatusCode === 'WITHDRAWN') return { label: 'Closed · Withdrawn (Manual)', tone: 'neutral', pulse: false };
    if ((kase as any).caseStatusCode === 'DISMISSED') return { label: 'Closed · Dismissed (Manual)', tone: 'danger',  pulse: false };
    return { label: 'Closed', tone: 'neutral', pulse: false };
  }
  if (kase.caseStatus === 'UNCLAIMED') {
    return { label: 'Open · Pending Claim', tone: 'warning', pulse: true };
  }
  // IN_PROGRESS — surface the EWF state.
  const stateName = kase.currentStateDisplayName ?? kase.currentStateCode ?? 'In Progress';
  // Broken commitment → highlight in danger tone; otherwise active.
  if (kase.currentStateCode === 'BROKEN') return { label: `Open · ${stateName}`, tone: 'danger', pulse: true };
  return { label: `Open · ${stateName}`, tone: 'active', pulse: true };
}

// ----------------------------------------------------------------------------

const PreLitigationCaseDetailModal: React.FC<PreLitigationCaseDetailModalProps> = ({
  isOpen, caseId, onClose,
  tabId, moduleName, onRestoreModal,
}) => {
  const permissions = useRecsuitPermissions();
  const { minimize } = useModalMinimize();

  const { data: kase, loading, error, refetch } = usePreLitCase(caseId ?? undefined);
  const preLitConfig = usePreLitConfig();

  const [activeTab, setActiveTab] = useState<TabId>('overview');
  // Refs for closure-stable values used by handleMinimize (mirrors Zone form
  // modal's pattern of capturing state at minimise time, not at deps time).
  const caseIdRef = useRef(caseId);
  caseIdRef.current = caseId;
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  // Sub-data: actions and commitments. Eager-loaded on open + after every trigger.
  const [actions, setActions] = useState<AgentAction[]>([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [commitments, setCommitments] = useState<Commitment[]>([]);
  const [commitmentsLoading, setCommitmentsLoading] = useState(false);

  // Unified activity grid — single "show all" toggle covering both
  // actions and commitments together.
  const [showAllActivity, setShowAllActivity] = useState(false);

  const fetchActions = useCallback(async () => {
    if (!caseId) return;
    setActionsLoading(true);
    try {
      const res = await preLitigationService.getActions(caseId);
      setActions(res.data ?? []);
    } catch {
      setActions([]);
    } finally {
      setActionsLoading(false);
    }
  }, [caseId]);

  const fetchCommitments = useCallback(async () => {
    if (!caseId) return;
    setCommitmentsLoading(true);
    try {
      const res = await preLitigationService.getCommitments(caseId);
      setCommitments(res.data ?? []);
    } catch {
      setCommitments([]);
    } finally {
      setCommitmentsLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    if (isOpen && caseId) {
      fetchActions();
      fetchCommitments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, caseId]);

  // Sub-modal targets
  const [logActionOpen, setLogActionOpen] = useState(false);
  const [captureCommitmentOpen, setCaptureCommitmentOpen] = useState(false);
  const [resolveTarget, setResolveTarget] = useState<Commitment | null>(null);
  const [escalateTarget, setEscalateTarget] = useState<EscalationTarget | null>(null);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);

  // UI: collapsible Basic Details (collapsed by default — the hero strip and
  // subtitle already give the at-a-glance values most agents need).
  const [basicDetailsOpen, setBasicDetailsOpen] = useState(false);

  const handleResolveCommitment = useCallback((c: Commitment) => {
    setResolveTarget(c);
  }, []);

  // ---- Minimise handler — mirrors ZoneFormModal's pattern ----
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentCaseId = caseIdRef.current;
    if (!currentCaseId) return;
    const currentActiveTab = activeTabRef.current;
    const currentCaseCode = kase?.caseCode;

    const modalId = `${tabId}-prelit-case-${currentCaseId}`;
    const title = currentCaseCode
      ? `Case · ${currentCaseCode}`
      : `Case · ${currentCaseId.slice(0, 8)}`;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      // The case detail itself has no form fields; nested sub-modals
      // (LogAction / CaptureCommitment / Resolve / Escalate / Reassign)
      // carry their own dirty tracking. Parent modal is never dirty.
      isDirty: false,
      onRestore: () => {
        if (onRestoreModal) onRestoreModal(currentCaseId, currentActiveTab);
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, kase?.caseCode]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('overview');
      setLogActionOpen(false);
      setCaptureCommitmentOpen(false);
      setResolveTarget(null);
      setEscalateTarget(null);
      setReassignOpen(false);
      setCloseOpen(false);
      setShowAllActivity(false);
    }
  }, [isOpen]);

  if (!isOpen || !caseId) return null;

  // Loading / error gates: render shell so the close button still works.
  if (loading && !kase) {
    return (
      <>
        <div className="modal-backdrop" />
        <div className="modal-container modal-xl">
          <div className="modal-header" style={{ minHeight: 'auto' }}>
            <h2 className="modal-title">Early Recovery Action</h2>
            <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
          </div>
          <div className="modal-content" style={{ overflow: 'auto' }}>
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              Loading case…
            </div>
          </div>
          <div className="modal-footer" style={{ minHeight: 'auto' }}>
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </>
    );
  }

  if (error || !kase) {
    return (
      <>
        <div className="modal-backdrop" />
        <div className="modal-container modal-xl">
          <div className="modal-header" style={{ minHeight: 'auto' }}>
            <h2 className="modal-title">Early Recovery Action</h2>
            <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
          </div>
          <div className="modal-content" style={{ overflow: 'auto' }}>
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--form-border-error, #ef4444)' }}>
                {error ? `Failed to load case: ${error.message}` : 'Case not found.'}
              </p>
            </div>
          </div>
          <div className="modal-footer" style={{ minHeight: 'auto' }}>
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </>
    );
  }

  const pill = caseStatusPill(kase);
  const isOpenCase = kase.caseStatus !== 'CLOSED';

  // Workflow progress is conveyed by the status pill ("Open · Commitment Given")
  // plus this compact SLA chip inline in the header — the full stepper row
  // was retired so the activity grid gets the vertical space.
  const slaDays = (kase.currentStateSlaDays ?? 0) > 0 ? kase.currentStateSlaDays! : null;
  const daysInState = kase.daysInCurrentState ?? 0;
  const isOverSla = slaDays != null && daysInState > slaDays;

  // Capture-commitment is enabled from ASSIGNED / CONTACTING / BROKEN. The
  // service auto-advances ASSIGNED → CONTACTING before recording.
  const canCapture = (kase.currentStateCode === 'CONTACTING'
                       || kase.currentStateCode === 'BROKEN'
                       || kase.currentStateCode === 'ASSIGNED')
                     && !kase.openCommitmentId;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">

        {/* ---- Header: title + pill (left) · Refresh + Close (right) ---- */}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap', minWidth: 0, flex: 1 }}>
            <h2 className="modal-title" style={{ margin: 0 }}>
              {kase.caseCode ?? ''}
            </h2>
            <StatusPill label={pill.label} tone={pill.tone} pulse={pill.pulse} dot size="sm" />
            {isOpenCase && kase.daysInCurrentState != null && slaDays != null && (
              <span style={{
                fontSize: '0.7rem', fontWeight: 600,
                color: isOverSla ? '#b91c1c' : '#15803d',
                background: isOverSla ? 'rgba(220, 38, 38, 0.08)' : 'rgba(22, 163, 74, 0.08)',
                border: `1px solid ${isOverSla ? '#dc2626' : '#16a34a'}`,
                padding: '2px 8px', borderRadius: '999px', whiteSpace: 'nowrap',
              }}>
                {isOverSla ? '⚠' : '✓'} {daysInState}d in state · SLA {slaDays}d
              </span>
            )}
            {kase.closedAt && (
              <span style={{
                fontSize: '0.7rem', color: 'var(--text-secondary)',
                padding: '2px 8px', borderRadius: '999px',
                border: '1px solid var(--border-color)', whiteSpace: 'nowrap',
              }}>
                Closed · {new Date(kase.closedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexShrink: 0 }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-header-btn" onClick={refetch} title="Refresh">
              <IconSystem.refresh size={16} />
            </button>
            <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content" style={{ overflow: 'auto' }}>
          <div style={{ padding: '0.65rem 1.1rem', display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>

            {/* ---- Hero KPI strip — Overdue, DPD, EMI Open, Open Commitment.
                 Always visible at the top because these answer "why are we
                 calling this borrower" before any scroll. ---- */}
            <HeroKpiStrip kase={kase} />

            {/* ---- Contact strip — name / role / phone / email. Frequently
                 needed during the customer interaction. ---- */}
            <ContactStrip kase={kase} />

            {/* ---- Collapsible Basic Details (closed by default) ---- */}
            <CollapsibleBasicDetails
              open={basicDetailsOpen}
              onToggle={() => setBasicDetailsOpen(o => !o)}
              kase={kase}
            />

            {/* ---- Empty-state ribbons for non-actionable cases ---- */}
            {!kase.workflowInstanceId && kase.caseStatus === 'UNCLAIMED' && (
              <div style={{
                padding: '0.55rem 1rem',
                border: '1px dashed var(--border-color)',
                borderRadius: '8px',
                background: 'var(--bg-secondary)',
                fontSize: '0.82rem',
                color: 'var(--text-secondary)',
              }}>
                This case is unclaimed. Claim it from the Pool screen to start the workflow.
              </div>
            )}
            {kase.workflowInstanceId && kase.caseStatus === 'CLOSED' && (
              <div style={{
                padding: '0.45rem 1rem',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                background: 'var(--bg-secondary)',
                fontSize: '0.78rem',
                color: 'var(--text-secondary)',
              }}>
                This case is closed. Expand the Timeline section below to see the workflow history.
              </div>
            )}

            {/* ---- Unified Activity grid — Actions + Open Commitments
                 interleaved, latest first. The Log Action + Capture
                 Commitment buttons live in this grid's header strip on the
                 right (semantically "add a new row to this list"). ---- */}
            <UnifiedActivity
              actions={actions}
              commitments={commitments}
              loading={actionsLoading || commitmentsLoading}
              showAll={showAllActivity}
              onToggleShowAll={() => setShowAllActivity(v => !v)}
              onResolveCommitment={handleResolveCommitment}
              permissions={permissions}
              canLogAction={!!(isOpenCase && kase.workflowInstanceId)}
              canCaptureCommitment={!!(isOpenCase && kase.workflowInstanceId && canCapture)}
              onLogAction={() => setLogActionOpen(true)}
              onCaptureCommitment={() => setCaptureCommitmentOpen(true)}
            />

            {/* ---- Secondary sections (collapsed by default) ---- */}
            <CollapsibleSection title="Overview" subtitle="Eligibility snapshot · claim snapshot · lifecycle">
              <OverviewTab kase={kase} />
            </CollapsibleSection>

            <CollapsibleSection title="Timeline" subtitle="Workflow transition history">
              {kase.workflowInstanceId
                ? <WorkflowTimeline instanceId={String(kase.workflowInstanceId)} />
                : <EmptyTabMessage text="Timeline appears once the case is claimed and the workflow starts." />}
            </CollapsibleSection>

            <CollapsibleSection title="Audit" subtitle="Field-level change log">
              <EmptyTabMessage text="Audit log viewer — Phase 8 deliverable." />
            </CollapsibleSection>
          </div>
        </div>

        {/* ---- Footer: case-level actions (Reassign / Close Case / Escalate)
             + Close. View Account 360 + All Cases were retired — they belong
             on the accounts module, not in the per-case detail. ---- */}
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          {isOpenCase && permissions.hasAction('pre-litigation-case', 'assign') && (
            <DomainBtn
              variant="neutral"
              icon={<IconSystem.users size={14} />}
              label="Reassign"
              onClick={() => setReassignOpen(true)}
            />
          )}
          {isOpenCase && permissions.hasAction('pre-litigation-case', 'close_case') && (
            <DomainBtn
              variant="neutral"
              icon={<IconSystem.checkCircle size={14} />}
              label="Close Case"
              onClick={() => setCloseOpen(true)}
            />
          )}
          {isOpenCase && permissions.hasAction('pre-litigation-case', 'escalate') && (
            <EscalateDropdown<EscalationTarget>
              options={[
                { target: 'ARBITRATION', label: 'Arbitration', description: 'Refer to arbitrator (unsecured / general)' },
                { target: 'SARFAESI',    label: 'SARFAESI',
                  description: kase.isSecured ? 'Enforce security under SARFAESI Act' : 'Requires a secured asset',
                  disabled: !kase.isSecured,
                  disabledReason: 'SARFAESI requires a secured asset' },
              ]}
              onSelect={(t) => setEscalateTarget(t)}
            />
          )}
          <div style={{ flex: 1 }} />
          <button className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>

      {/* ---- Sub-modals ---- */}
      <LogAgentActionModal
        isOpen={logActionOpen}
        caseId={String(kase.id)}
        caseCode={kase.caseCode}
        currentStateCode={kase.currentStateCode}
        maxBackdateDays={preLitConfig.maxBackdateDays}
        commitmentWindowDays={preLitConfig.commitmentWindowDays}
        defaultActionType={kase.nextActionType}
        onClose={() => setLogActionOpen(false)}
        onLogged={() => {
          setLogActionOpen(false);
          refetch(); fetchActions(); fetchCommitments();
        }}
      />

      <CaptureCommitmentModal
        isOpen={captureCommitmentOpen}
        caseId={String(kase.id)}
        caseCode={kase.caseCode}
        commitmentWindowDays={preLitConfig.commitmentWindowDays}
        onClose={() => setCaptureCommitmentOpen(false)}
        onCaptured={() => {
          setCaptureCommitmentOpen(false);
          refetch(); fetchActions(); fetchCommitments();
          notificationService.toast.success('Commitment captured.');
        }}
      />

      <ResolveCommitmentModal
        isOpen={!!resolveTarget}
        commitment={resolveTarget}
        caseCode={kase.caseCode}
        onClose={() => setResolveTarget(null)}
        onResolved={() => {
          setResolveTarget(null);
          refetch(); fetchActions(); fetchCommitments();
        }}
      />

      {escalateTarget && (
        <EscalateCaseModal
          isOpen={!!escalateTarget}
          caseId={String(kase.id)}
          caseCode={kase.caseCode}
          targetType={escalateTarget}
          isSecured={!!kase.isSecured}
          currentOutstanding={kase.currentOutstanding ?? 0}
          onClose={() => setEscalateTarget(null)}
          onEscalated={() => { setEscalateTarget(null); refetch(); }}
        />
      )}

      <AssignAgentModal
        isOpen={reassignOpen}
        caseId={String(kase.id)}
        caseCode={kase.caseCode}
        branchId={kase.branchId}
        currentAgentName={kase.assignedAgentName ?? null}
        onClose={() => setReassignOpen(false)}
        onAssigned={() => { setReassignOpen(false); refetch(); }}
      />

      <CloseEarlyRecoveryModal
        isOpen={closeOpen}
        caseId={String(kase.id)}
        caseCode={kase.caseCode}
        onClose={() => setCloseOpen(false)}
        onClosed={() => { setCloseOpen(false); refetch(); fetchActions(); fetchCommitments(); }}
      />
    </>
  );
};

// ============================================================================
// SUB-COMPONENTS — local to this modal
// ============================================================================

type DomainBtnVariant = 'primary' | 'success' | 'neutral';

/** Visual palette aligned with EscalateDropdown's filled-tint pattern
 *  (light bg + bold border + dark text) so all four action buttons read as a set. */
const DOMAIN_BTN_PALETTE: Record<DomainBtnVariant, { bg: string; bgHover: string; border: string; text: string }> = {
  primary: { bg: '#dbeafe', bgHover: '#bfdbfe', border: '#2563eb', text: '#1e3a8a' }, // Log Action — most common
  success: { bg: '#dcfce7', bgHover: '#bbf7d0', border: '#16a34a', text: '#166534' }, // Capture Commitment — positive outcome
  neutral: { bg: '#e2e8f0', bgHover: '#cbd5e1', border: '#64748b', text: '#334155' }, // Reassign — operational
};

const DomainBtn: React.FC<{
  variant?: DomainBtnVariant;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}> = ({ variant = 'primary', icon, label, onClick, disabled, title }) => {
  const p = DOMAIN_BTN_PALETTE[variant];
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={disabled}
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
        padding: '0.5rem 0.95rem', fontSize: '0.85rem', fontWeight: 600,
        border: `1px solid ${p.border}`,
        borderRadius: '6px',
        background: disabled ? '#f1f5f9' : (hover ? p.bgHover : p.bg),
        color: disabled ? 'var(--text-secondary)' : p.text,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'background 0.15s, transform 0.05s',
        transform: hover && !disabled ? 'translateY(-1px)' : 'none',
        boxShadow: hover && !disabled ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none',
      }}
    >
      {icon}
      {label}
    </button>
  );
};

// ----- Hero KPI strip — top-of-modal at-a-glance KPIs -----
//
// Overdue and Outstanding are SEPARATE values:
//   - Overdue     = past-due EMI portion (currentOverdueAmount on the case DTO,
//                   sourced from acct.tbl_account.current_overdue_amount).
//   - Outstanding = full loan book value (currentOutstanding — outstanding_total).
// Overdue is always <= Outstanding.

const HeroKpiStrip: React.FC<{ kase: PreLitCase }> = ({ kase }) => {
  const overdue = kase.currentOverdueAmount ?? 0;
  const os      = kase.currentOutstanding   ?? 0;
  const dpd     = kase.currentDpd           ?? 0;

  // Severity colour for DPD (RBI bands).
  const dpdColour = dpd >= 90 ? '#dc2626' : dpd >= 60 ? '#d97706' : dpd >= 30 ? '#ca8a04' : '#16a34a';
  const dpdLabel  = dpd >= 90 ? 'NPA RISK' : dpd >= 60 ? 'SEVERE'  : dpd >= 30 ? 'WATCH'  : 'EARLY';

  // Overdue-as-% of outstanding — gives the agent an instant sense of severity.
  const overduePct = os > 0 ? (overdue / os) * 100 : 0;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(165px, 1fr))',
      gap: '0.55rem',
      padding: '0.55rem 0.75rem',
      border: '1px solid var(--border-color)',
      borderRadius: '8px',
      background: 'linear-gradient(180deg, #fef2f2 0%, var(--bg-primary) 70%)',
    }}>
      {/* Overdue — past-due EMI portion. The primary "ask for money" number. */}
      <HeroKpi
        label="Overdue"
        value={`₹${overdue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
        valueColour="#b91c1c"
        sub={overdue > 0 ? `${overduePct.toFixed(1)}% of OS` : 'no past-due EMI'}
      />
      {/* Outstanding — full book value. The exposure number. */}
      <HeroKpi
        label="Outstanding"
        value={`₹${os.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
        valueColour="#1e293b"
        sub="Book value"
      />
      {/* DPD with severity badge */}
      <HeroKpi
        label="DPD"
        value={String(dpd)}
        valueColour={dpdColour}
        badge={<span style={{
          fontSize: '0.58rem', fontWeight: 700, color: dpdColour,
          border: `1px solid ${dpdColour}`, padding: '1px 5px', borderRadius: 999,
          marginLeft: '0.35rem', whiteSpace: 'nowrap', letterSpacing: '0.04em',
        }}>{dpdLabel}</span>}
      />
      {/* Open commitment, if any */}
      {kase.openCommitmentAmount != null && (
        <HeroKpi
          label="Open Commitment"
          value={`₹${(kase.openCommitmentAmount ?? 0).toLocaleString('en-IN')}`}
          valueColour="#1e3a8a"
          sub={kase.openCommitmentDate ? `by ${formatShortDate(kase.openCommitmentDate)}` : undefined}
        />
      )}
    </div>
  );
};

const HeroKpi: React.FC<{
  label: string;
  value: string;
  valueColour?: string;
  badge?: React.ReactNode;
  sub?: string;
}> = ({ label, value, valueColour, badge, sub }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.05rem', minWidth: 0 }}>
    <span style={{
      fontSize: '0.6rem', fontWeight: 700, color: 'var(--text-secondary)',
      textTransform: 'uppercase', letterSpacing: '0.06em',
    }}>
      {label}
    </span>
    <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}>
      <span style={{
        fontSize: '1.15rem', fontWeight: 700,
        color: valueColour ?? 'var(--text-primary)',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        lineHeight: 1.15,
      }}>
        {value}
      </span>
      {badge}
    </div>
    {sub && (
      <span style={{ fontSize: '0.66rem', color: 'var(--text-secondary)', lineHeight: 1.2 }}>{sub}</span>
    )}
  </div>
);

// ----- Contact strip — name + role + phone(s) + email -----
//
// Sourced from acct.tbl_customer.{mobile_primary, mobile_alternate, email} +
// acct.tbl_account_party.party_role for the PRIMARY borrower via the case
// detail JOIN. When the LMS import hasn't populated mobile/email yet, the
// cells render "—" gracefully.

const PARTY_ROLE_LABEL: Record<string, string> = {
  PRIMARY:      'Primary Borrower',
  CO_BORROWER:  'Co-Borrower',
  GUARANTOR:    'Guarantor',
};

const ContactStrip: React.FC<{ kase: PreLitCase }> = ({ kase }) => {
  const mobile     = kase.borrowerMobilePrimary;
  const altMobile  = kase.borrowerMobileAlternate;
  const email      = kase.borrowerEmail;
  const roleLabel  = PARTY_ROLE_LABEL[kase.primaryPartyRole ?? 'PRIMARY'] ?? (kase.primaryPartyRole ?? 'Primary Borrower');

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '0.55rem',
      padding: '0.5rem 0.75rem',
      border: '1px solid var(--border-color)',
      borderRadius: '8px',
      background: 'var(--bg-primary)',
    }}>
      <ContactCell
        icon={<IconSystem.user size={12} />}
        label="Contact"
        primary={kase.customerName}
        secondary={roleLabel}
      />
      <ContactCell
        icon={<IconSystem.message size={12} />}
        label="Mobile"
        primary={mobile ?? '—'}
        secondary={altMobile ? `Alt: ${altMobile}` : undefined}
        copyable={mobile}
      />
      <ContactCell
        icon={<IconSystem.mail size={12} />}
        label="Email"
        primary={email ?? '—'}
        copyable={email}
      />
      <ContactCell
        icon={<IconSystem.users size={12} />}
        label="Agent"
        primary={kase.assignedAgentName ?? '—'}
      />
    </div>
  );
};

const ContactCell: React.FC<{
  icon: React.ReactNode;
  label: string;
  primary: string;
  secondary?: string;
  copyable?: string;
}> = ({ icon, label, primary, secondary, copyable }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.02rem', minWidth: 0 }}>
    <span style={{
      fontSize: '0.58rem', fontWeight: 700, color: 'var(--text-secondary)',
      textTransform: 'uppercase', letterSpacing: '0.06em',
      display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
    }}>
      {icon}
      {label}
    </span>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', minWidth: 0 }}>
      <span style={{
        fontSize: '0.82rem', fontWeight: 600,
        color: primary === '—' ? 'var(--text-muted)' : 'var(--text-primary)',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        lineHeight: 1.2,
      }}>
        {primary}
      </span>
      {copyable && (
        <button
          type="button"
          onClick={() => navigator.clipboard?.writeText(copyable)}
          title="Copy"
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            padding: 0, color: 'var(--text-secondary)', lineHeight: 0,
          }}
        >
          <IconSystem.copy size={11} />
        </button>
      )}
    </div>
    {secondary && (
      <span style={{ fontSize: '0.66rem', color: 'var(--text-secondary)', lineHeight: 1.2 }}>{secondary}</span>
    )}
  </div>
);

// ----- Collapsible Basic Details — closed by default -----

const CollapsibleBasicDetails: React.FC<{
  open: boolean;
  onToggle: () => void;
  kase: PreLitCase;
}> = ({ open, onToggle, kase }) => (
  <div style={{
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    background: 'var(--bg-primary)',
    overflow: 'hidden',
  }}>
    <button
      type="button"
      onClick={onToggle}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%',
        padding: '0.55rem 0.85rem',
        background: 'transparent', border: 'none', cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <IconSystem.chevronRight
        size={14}
        style={{
          transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s',
          color: 'var(--text-secondary)',
        }}
      />
      <span style={{
        fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)',
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }}>
        Basic Details
      </span>
      <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginLeft: '0.25rem' }}>
        {kase.customerName} · {kase.accountNumber}
        {kase.productCode ? ` · ${kase.productCode}` : ''}
        {kase.branchCode ? ` · ${kase.branchCode}` : ''}
      </span>
    </button>
    {open && (
      <div style={{ borderTop: '1px solid var(--border-color)' }}>
        <CaseSummaryGrid kase={kase} />
      </div>
    )}
  </div>
);

// ----- Flat responsive field grid (replaces the collapsible CaseSummaryCard) -----

const CaseSummaryGrid: React.FC<{ kase: PreLitCase }> = ({ kase }) => {
  const fields: Array<{ label: string; value: React.ReactNode }> = [
    { label: 'Account #',      value: kase.accountNumber },
    { label: 'Customer',       value: kase.customerName },
    { label: 'Product',        value: kase.productCode ?? '—' },
    { label: 'Branch',         value: kase.branchCode ?? '—' },
    { label: 'DPD (now)',      value: String(kase.currentDpd ?? 0) },
    { label: 'Outstanding (now)', value: `₹${(kase.currentOutstanding ?? 0).toLocaleString('en-IN')}` },
    { label: 'Secured',
      value: kase.isSecured
        ? <StatusPill label="Yes" tone="info"    size="xs" />
        : <StatusPill label="No"  tone="neutral" size="xs" />,
    },
    { label: 'Assigned Agent', value: kase.assignedAgentName ?? '—' },
    { label: 'Eligible Since', value: kase.openedAt  ? formatShortDate(kase.openedAt)  : '—' },
    { label: 'Claimed At',     value: kase.claimedAt ? formatShortDate(kase.claimedAt) : '—' },
  ];
  if (kase.closedAt) {
    fields.push({ label: 'Closed At',      value: formatShortDate(kase.closedAt) });
    fields.push({ label: 'Closure Reason', value: kase.closureReasonName ?? '—' });
  }

  return (
    <div
      style={{
        // Border / radius / background come from the CollapsibleBasicDetails
        // wrapper; this grid is the inner content only.
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '0.5rem 1rem',
        padding: '0.75rem 1rem',
      }}
    >
      {fields.map(f => (
        <div key={f.label} style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem', minWidth: 0 }}>
          <span style={{
            fontSize: '0.65rem', fontWeight: 600,
            color: 'var(--text-secondary)',
            textTransform: 'uppercase', letterSpacing: '0.04em',
          }}>
            {f.label}
          </span>
          <span style={{
            fontSize: '0.85rem',
            color: 'var(--text-primary)',
            fontWeight: 500,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {f.value}
          </span>
        </div>
      ))}
    </div>
  );
};

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const SectionHeading: React.FC<{ text: string }> = ({ text }) => (
  <div style={{
    fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-secondary)',
    textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem',
  }}>
    {text}
  </div>
);

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0', fontSize: '0.85rem' }}>
    <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
    <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{value}</span>
  </div>
);

const EmptyTabMessage: React.FC<{ text: string }> = ({ text }) => (
  <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
    {text}
  </div>
);

// (PrimaryActionBar retired — its Log Action + Capture Commitment buttons
//  now live inside the Activity grid's header strip, where "add a new row"
//  is the natural reading of the + Log Action button.)

// ============================================================================
// UNIFIED ACTIVITY — actions + open commitments interleaved
// ============================================================================
//
// Single chronological list of every interaction on the case. Discriminates
// each row visually (action vs commitment) via a small colored chip on the
// left. ~4 rows render above the fold; the rest expand on demand.

const ACTIVITY_DEFAULT_VISIBLE = 4;

interface ActivityRow {
  kind: 'action' | 'commitment';
  when: string;
  /** Discriminated payload — the renderer reads only what it needs. */
  action?: AgentAction;
  commitment?: Commitment;
}

const UnifiedActivity: React.FC<{
  actions: AgentAction[];
  commitments: Commitment[];
  loading: boolean;
  showAll: boolean;
  onToggleShowAll: () => void;
  onResolveCommitment: (c: Commitment) => void;
  permissions: ReturnType<typeof useRecsuitPermissions>;
  /** When true, render the "+ Log Action" header button (gated by permission internally). */
  canLogAction: boolean;
  /** When true, render the "Capture Commitment" header button (gated by permission internally). */
  canCaptureCommitment: boolean;
  onLogAction: () => void;
  onCaptureCommitment: () => void;
}> = ({
  actions, commitments, loading, showAll, onToggleShowAll,
  onResolveCommitment, permissions,
  canLogAction, canCaptureCommitment, onLogAction, onCaptureCommitment,
}) => {
  const rows = useMemo<ActivityRow[]>(() => {
    const list: ActivityRow[] = [
      ...actions.map(a => ({ kind: 'action' as const, when: a.actedAt, action: a })),
      ...commitments.map(c => ({
        kind: 'commitment' as const,
        when: c.committedAt || c.committedDate || '',
        commitment: c,
      })),
    ];
    return list
      .filter(r => r.when)
      .sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime());
  }, [actions, commitments]);

  const visible = showAll ? rows : rows.slice(0, ACTIVITY_DEFAULT_VISIBLE);
  const hidden  = rows.length - visible.length;

  const canResolve = permissions.hasAction('pre-litigation-case', 'resolve_commitment');

  return (
    <div style={{
      border: '1px solid var(--border-color)',
      borderRadius: '8px',
      background: 'var(--bg-primary)',
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.6rem',
        padding: '0.45rem 0.55rem 0.45rem 0.75rem',
        background: 'var(--bg-secondary)',
        borderBottom: '1px solid var(--border-color)',
        minHeight: 40,
      }}>
        {/* Title block — left side */}
        <span style={{
          fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-primary)',
          textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>
          Activity
        </span>
        <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
          {actions.length} action{actions.length === 1 ? '' : 's'} · {commitments.length} commitment{commitments.length === 1 ? '' : 's'}
        </span>

        {/* Show-all toggle slips next to the count */}
        {hidden > 0 && !showAll && (
          <button type="button" onClick={onToggleShowAll} style={ACTIVITY_LINK_BTN}>
            Show all ({rows.length})
          </button>
        )}
        {showAll && rows.length > ACTIVITY_DEFAULT_VISIBLE && (
          <button type="button" onClick={onToggleShowAll} style={ACTIVITY_LINK_BTN}>
            Show last {ACTIVITY_DEFAULT_VISIBLE}
          </button>
        )}

        <div style={{ flex: 1 }} />

        {/* Right-side CTAs — "+ Log Action" sized like a real button, not a banner. */}
        {canCaptureCommitment && permissions.hasAction('pre-litigation-case', 'capture_commitment') && (
          <button
            type="button"
            onClick={onCaptureCommitment}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
              padding: '0.35rem 0.7rem', fontSize: '0.78rem', fontWeight: 600,
              background: '#dcfce7', color: '#166534',
              border: '1px solid #16a34a', borderRadius: '6px',
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            <IconSystem.checkCircle size={13} />
            Capture Commitment
          </button>
        )}
        {canLogAction && permissions.hasAction('pre-litigation-case', 'log_action') && (
          <button
            type="button"
            onClick={onLogAction}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
              padding: '0.4rem 0.85rem', fontSize: '0.82rem', fontWeight: 700,
              background: '#2563eb', color: '#fff',
              border: '1px solid #1d4ed8', borderRadius: '6px',
              cursor: 'pointer', whiteSpace: 'nowrap',
              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
            }}
          >
            <IconSystem.add size={14} />
            Log Action
          </button>
        )}
      </div>

      {loading && rows.length === 0 ? (
        <div style={{ padding: '1.25rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          Loading activity…
        </div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '1.5rem 1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
          <IconSystem.message size={28} style={{ opacity: 0.3, marginBottom: '0.45rem' }} />
          <div style={{ fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.15rem' }}>No interactions yet</div>
          <div style={{ fontSize: '0.75rem' }}>Tap <strong>Log Action</strong> above to record the first call.</div>
        </div>
      ) : (
        <table style={{
          width: '100%', borderCollapse: 'separate', borderSpacing: 0,
          fontSize: '0.78rem', tableLayout: 'fixed',
        }}>
          <colgroup>
            <col style={{ width: 110 }} />
            <col style={{ width: 130 }} />
            <col style={{ width: 165 }} />
            <col />
            <col style={{ width: 130 }} />
            <col style={{ width: 90 }} />
          </colgroup>
          <thead>
            <tr style={{ background: '#f1f5f9' }}>
              <th style={ACT_TH}>Type</th>
              <th style={ACT_TH}>When</th>
              <th style={ACT_TH}>Disposition / Amount</th>
              <th style={ACT_TH}>Notes / Status</th>
              <th style={ACT_TH}>By</th>
              <th style={{ ...ACT_TH, textAlign: 'right' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r, idx) => (
              <ActivityRowItem
                key={`${r.kind}-${r.action?.id ?? r.commitment?.id ?? idx}`}
                row={r}
                zebra={idx % 2 === 1}
                canResolve={canResolve}
                onResolveCommitment={onResolveCommitment}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const ACTIVITY_LINK_BTN: React.CSSProperties = {
  background: 'transparent', border: 'none', cursor: 'pointer',
  color: '#2563eb', fontSize: '0.72rem', fontWeight: 600, padding: 0,
};

const ACT_TH: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.55rem 0.7rem',
  fontSize: '0.62rem',
  fontWeight: 700,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  borderBottom: '1px solid var(--border-color)',
  whiteSpace: 'nowrap',
};

const ACT_TD: React.CSSProperties = {
  padding: '0.55rem 0.7rem',
  borderBottom: '1px solid var(--border-color)',
  verticalAlign: 'middle',
};

const ActivityRowItem: React.FC<{
  row: ActivityRow;
  zebra: boolean;
  canResolve: boolean;
  onResolveCommitment: (c: Commitment) => void;
}> = ({ row, zebra, canResolve, onResolveCommitment }) => {
  // Subtle zebra for readability; OPEN commitments override with a soft green.
  const baseBg = zebra ? '#fafafa' : 'var(--bg-primary)';

  if (row.kind === 'action' && row.action) {
    const a = row.action;
    return (
      <tr style={{ background: baseBg }}>
        <td style={ACT_TD}>
          <Chip text={a.actionType ?? 'ACTION'} color="#2563eb" tint="#dbeafe" />
        </td>
        <td style={{ ...ACT_TD, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {new Date(a.actedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </td>
        <td style={{ ...ACT_TD, color: 'var(--text-primary)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {(a.disposition ?? '—').replace(/_/g, ' ')}
        </td>
        <td style={{ ...ACT_TD, color: 'var(--text-secondary)', overflow: 'hidden' }}>
          {a.notes
            ? (
              <span
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: 1.35,
                }}
                title={a.notes}
              >
                {a.notes}
              </span>
            )
            : <span style={{ color: 'var(--text-muted)' }}>—</span>}
        </td>
        <td style={{ ...ACT_TD, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {a.actedByName ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}
        </td>
        <td style={{ ...ACT_TD, textAlign: 'right' }}>
          <span style={{ color: 'var(--text-muted)' }}>—</span>
        </td>
      </tr>
    );
  }
  if (row.kind === 'commitment' && row.commitment) {
    const c = row.commitment;
    const status = c.honouredStatus ?? 'OPEN';
    const isOpen = status === 'OPEN';
    return (
      <tr style={{ background: isOpen ? 'rgba(34, 197, 94, 0.06)' : baseBg }}>
        <td style={ACT_TD}>
          <Chip text="COMMITMENT" color="#15803d" tint="#dcfce7" />
        </td>
        <td style={{ ...ACT_TD, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {new Date(c.committedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </td>
        <td style={{ ...ACT_TD, color: '#166534', fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
          ₹{Number(c.committedAmount ?? 0).toLocaleString('en-IN')}
        </td>
        <td style={{ ...ACT_TD, whiteSpace: 'nowrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
            <span style={{
              alignSelf: 'flex-start',
              fontSize: '0.6rem', fontWeight: 700,
              color: isOpen ? '#15803d' : 'var(--text-secondary)',
              background: isOpen ? '#fff' : 'transparent',
              border: `1px solid ${isOpen ? '#16a34a' : 'var(--border-color)'}`,
              padding: '1px 6px', borderRadius: 999, letterSpacing: '0.04em',
            }}>{status}</span>
            {c.committedDate && (
              <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                due {formatShortDate(c.committedDate)}
              </span>
            )}
          </div>
        </td>
        <td style={{ ...ACT_TD, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {c.capturedByName ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}
        </td>
        <td style={{ ...ACT_TD, textAlign: 'right' }}>
          {isOpen && canResolve ? (
            <button
              type="button"
              onClick={() => onResolveCommitment(c)}
              style={{
                fontSize: '0.72rem', fontWeight: 600,
                padding: '0.3rem 0.65rem',
                background: '#16a34a', color: '#fff',
                border: '1px solid #15803d', borderRadius: 5,
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              Resolve
            </button>
          ) : (
            <span style={{ color: 'var(--text-muted)' }}>—</span>
          )}
        </td>
      </tr>
    );
  }
  return null;
};

const Chip: React.FC<{ text: string; color: string; tint: string }> = ({ text, color, tint }) => (
  <span style={{
    fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.04em',
    color, background: tint,
    border: `1px solid ${color}`,
    padding: '2px 6px', borderRadius: 4,
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    display: 'inline-block',
  }}>{text}</span>
);

// ============================================================================
// COLLAPSIBLE SECTION — generic accordion for secondary content
// ============================================================================

const CollapsibleSection: React.FC<{
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, subtitle, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{
      border: '1px solid var(--border-color)',
      borderRadius: '8px',
      background: 'var(--bg-primary)',
      overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%',
          padding: '0.5rem 0.75rem',
          background: 'transparent', border: 'none', cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <IconSystem.chevronRight
          size={13}
          style={{
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s',
            color: 'var(--text-secondary)',
          }}
        />
        <span style={{
          fontSize: '0.74rem', fontWeight: 700, color: 'var(--text-primary)',
          textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>
          {title}
        </span>
        {subtitle && (
          <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
            {subtitle}
          </span>
        )}
      </button>
      {open && (
        <div style={{ borderTop: '1px solid var(--border-color)', padding: '0.75rem 1rem' }}>
          {children}
        </div>
      )}
    </div>
  );
};

// ---- Overview tab ----
//
// Restructured 2026-06-04 to lead with the action-oriented blocks the agent
// actually uses during a call (Open Commitment + Last Action quick read).
// The immutable snapshot tables follow underneath.

const OverviewTab: React.FC<{ kase: PreLitCase }> = ({ kase }) => {
  const dpdDelta = (kase.currentDpd ?? 0) - (kase.openedDpd ?? 0);
  const outstandingDelta = (kase.openedOutstanding ?? 0) - (kase.currentOutstanding ?? 0);

  return (
    <div style={{ padding: '1rem 0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* ---- Action-oriented strip — what the agent looks at on every call ---- */}
      {(kase.openCommitmentAmount != null || kase.lastActionAt) && (
        <div className="form-grid-2" style={{ gap: '1rem' }}>
          {/* Open commitment block */}
          {kase.openCommitmentAmount != null && (
            <div style={{
              border: '1px solid #16a34a', background: '#f0fdf4',
              borderRadius: '8px', padding: '0.85rem 1rem',
            }}>
              <SectionHeading text="Open Commitment" />
              <Field label="Amount"   value={`₹${(kase.openCommitmentAmount ?? 0).toLocaleString('en-IN')}`} />
              {kase.openCommitmentDate && (
                <Field label="Due by"   value={formatShortDate(kase.openCommitmentDate)} />
              )}
              <div style={{
                fontSize: '0.72rem', color: '#15803d', marginTop: '0.4rem',
              }}>
                Use the <strong>Resolve Commitment</strong> action to record receipt or close this commitment.
              </div>
            </div>
          )}
          {/* Last action quick-read */}
          {kase.lastActionAt && (
            <div style={{
              border: '1px solid #f59e0b', background: '#fef3c7',
              borderRadius: '8px', padding: '0.85rem 1rem',
            }}>
              <SectionHeading text="Last Action — Quick Read" />
              <div style={{ fontSize: '0.82rem', color: '#92400e' }}>
                <strong>{kase.lastActionType ?? '—'}</strong>
                {kase.lastActionDisposition ? ` · ${kase.lastActionDisposition}` : ''}
                {' · '}{new Date(kase.lastActionAt).toLocaleString('en-IN')}
              </div>
              {kase.lastActionNotes && (
                <div style={{
                  fontSize: '0.82rem', marginTop: '0.4rem',
                  whiteSpace: 'pre-wrap', color: '#78350f',
                }}>
                  {kase.lastActionNotes}
                </div>
              )}
              {kase.nextActionAt && (
                <div style={{ fontSize: '0.72rem', color: '#92400e', marginTop: '0.4rem' }}>
                  Next: <strong>{kase.nextActionType ?? '—'}</strong>
                  {' · '}{formatShortDate(kase.nextActionAt)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ---- Immutable snapshot tables (secondary — pushed below) ---- */}
      <div className="form-grid-2">
        <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '1rem' }}>
          <SectionHeading text="Eligibility Snapshot (immutable)" />
          <Field label="Opened At"               value={kase.openedAt ? new Date(kase.openedAt).toLocaleString('en-IN') : '—'} />
          <Field label="DPD"                     value={String(kase.openedDpd)} />
          <Field label="Outstanding"             value={`₹${(kase.openedOutstanding ?? 0).toLocaleString('en-IN')}`} />
          <Field label="EMI Outstanding Count"   value={String(kase.openedEmiOutstandingCount)} />
          <Field label="EMI Outstanding Amount"  value={`₹${(kase.openedEmiOutstandingAmount ?? 0).toLocaleString('en-IN')}`} />

          <div style={{ marginTop: '1rem' }}>
            <SectionHeading text="Claim Snapshot (immutable)" />
            <Field label="Claimed At"             value={kase.claimedAt ? new Date(kase.claimedAt).toLocaleString('en-IN') : '—'} />
            <Field label="DPD"                    value={kase.claimDpd != null ? String(kase.claimDpd) : '—'} />
            <Field label="Outstanding"            value={kase.claimOutstanding != null ? `₹${kase.claimOutstanding.toLocaleString('en-IN')}` : '—'} />
            <Field label="EMI Outstanding Count"  value={kase.claimEmiOutstandingCount != null ? String(kase.claimEmiOutstandingCount) : '—'} />
            <Field label="EMI Outstanding Amount" value={kase.claimEmiOutstandingAmount != null ? `₹${kase.claimEmiOutstandingAmount.toLocaleString('en-IN')}` : '—'} />
          </div>
        </div>
        <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '1rem' }}>
          <SectionHeading text="Lifecycle" />
          <Field label="Pool Status"                  value={kase.caseStatus} />
          <Field label="Workflow State"               value={kase.currentStateDisplayName ?? kase.currentStateCode ?? '—'} />
          <Field label="State Entered At"             value={kase.stateEnteredAt ? new Date(kase.stateEnteredAt).toLocaleString('en-IN') : '—'} />
          <Field label="Days in Current State"        value={kase.daysInCurrentState != null ? String(kase.daysInCurrentState) : '—'} />
          {kase.closedAt && (
            <>
              <Field label="Closed At"                value={new Date(kase.closedAt).toLocaleString('en-IN')} />
              <Field label="Closure Reason"           value={kase.closureReasonName ?? '—'} />
            </>
          )}

          <div style={{ marginTop: '1rem' }}>
            <SectionHeading text="Delta vs Eligibility Snapshot" />
            <Field
              label="DPD Movement"
              value={dpdDelta === 0 ? 'No change' : `${dpdDelta > 0 ? '+' : ''}${dpdDelta} days`}
            />
            <Field
              label="Recovered"
              value={outstandingDelta > 0
                ? `₹${outstandingDelta.toLocaleString('en-IN')}`
                : outstandingDelta < 0
                  ? `Outstanding grew by ₹${Math.abs(outstandingDelta).toLocaleString('en-IN')}`
                  : 'No change'}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// (Old ActionsTab / CommitmentsTab were retired with the tab strip — the
//  UnifiedActivity component above now renders both action and commitment
//  rows in a single chronological list.)

export default PreLitigationCaseDetailModal;
